import { ROUTES } from '@/lib/routes'

/** F-199: what the login's 403 `code: ERASURE_SCHEDULED` carries. */
export interface ErasureBlock {
  erasureScheduledAt: string | null
  /** `false` when the team erased at the person's request: there is no way back. */
  cancelable: boolean
  /** The API's sentence, in the request's language. */
  message: string | null
}

type Failure = { response?: { status?: number; data?: { code?: string; message?: string; data?: { erasureScheduledAt?: string; cancelable?: boolean } } } } | null | undefined

/** The login refused because the account's erasure is scheduled (no paid access), or `null`. */
export const erasureBlockOf = (e: unknown): ErasureBlock | null => {
  const res = (e as Failure)?.response
  if (res?.status !== 403 || res.data?.code !== 'ERASURE_SCHEDULED') return null
  return {
    erasureScheduledAt: res.data.data?.erasureScheduledAt ?? null,
    cancelable: res.data.data?.cancelable !== false,
    message: res.data.message ?? null
  }
}

/** `/account-erasure/cancel` (as the e-mail writes it) or `/_/account-erasure/cancel`. */
export const isAccountErasureCancelPath = (path: string): boolean => {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '')
  return clean === ROUTES.accountErasureCancel || clean === `/_${ROUTES.accountErasureCancel}`
}

/** Who is above whom here, for the only rule the API enforces on these routes: a role below one's own. */
const RANK: Record<string, number> = { member: 0, finance: 1, moderator: 1, admin: 2, owner: 3 }

/**
 * Whether the team actions show for this person: owner and admin (and the
 * SuperAdmin) over a role below their own, never the owner nor oneself. Only
 * hides what the API would refuse anyway (`POST /api/privacy/members/{id}/…`
 * answers 403); the answer is still the API's.
 */
export const canActOnMemberData = ({ viewerRole, isSuperAdmin, viewerId, targetId, targetRole }: {
  viewerRole?: string | null; isSuperAdmin?: boolean; viewerId?: string | null; targetId: string; targetRole?: string | null
}): boolean => {
  if (!targetRole || targetRole === 'owner' || targetId === viewerId) return false
  if (isSuperAdmin) return true
  if (viewerRole !== 'owner' && viewerRole !== 'admin') return false
  return (RANK[targetRole] ?? 0) < RANK[viewerRole]
}
